import { FC, useLayoutEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  WhatsappLogoIcon,
  LinkBreakIcon,
  LinkIcon,
} from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ReusableModal from '../common/Modal';
import { useAgents } from '../../hooks/useAgents';
import {
  connectWhatsappVoiceLinkApi,
  disconnectWhatsappVoiceLinkApi,
} from '../../utils/apis';

interface ConnectWhatsappAgentModalProps {
  open: boolean;
  onClose: () => void;
  voiceAgentId: string;
  linkedWhatsappAgentId?: string | null;
  onSuccess?: (message: string) => void;
}

const ConnectWhatsappAgentModal: FC<ConnectWhatsappAgentModalProps> = ({
  open,
  onClose,
  voiceAgentId,
  linkedWhatsappAgentId,
  onSuccess,
}) => {
  const isConnected = Boolean(linkedWhatsappAgentId);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { agents, isLoading } = useAgents();

  const whatsappAgents =
    agents?.agents?.filter(
      (agent: any) => agent.agentChannel === 'whatsapp' && agent.agentName
    ) ?? [];

  const connectedAgentName =
    whatsappAgents.find((a: any) => a._id === linkedWhatsappAgentId)
      ?.agentName ?? linkedWhatsappAgentId;

  useLayoutEffect(() => {
    if (open) {
      setSelectedAgentId('');
      setInlineError(null);
    }
  }, [open]);

  const { mutateAsync: connectAgent, isPending: isConnectPending } =
    useMutation({
      mutationFn: connectWhatsappVoiceLinkApi,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        onSuccess?.('WhatsApp agent connected successfully.');
        onClose();
      },
      onError: (error: any) => {
        setInlineError(
          error?.response?.data?.message ?? 'Failed to connect WhatsApp agent'
        );
      },
    });

  const { mutateAsync: disconnectAgent, isPending: isDisconnectPending } =
    useMutation({
      mutationFn: disconnectWhatsappVoiceLinkApi,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        onSuccess?.('WhatsApp agent disconnected successfully.');
        onClose();
      },
      onError: (error: any) => {
        setInlineError(
          error?.response?.data?.message ??
            'Failed to disconnect WhatsApp agent'
        );
      },
    });

  const handleConnect = async () => {
    if (!selectedAgentId) return;
    setInlineError(null);
    try {
      await connectAgent({ voiceAgentId, whatsappAgentId: selectedAgentId });
    } catch {
      // surfaced via onError
    }
  };

  const handleDisconnect = async () => {
    setInlineError(null);
    try {
      await disconnectAgent({ voiceAgentId });
    } catch {
      // surfaced via onError
    }
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title={
        isConnected ? 'Disconnect WhatsApp Agent' : 'Connect WhatsApp Agent'
      }
      width={520}
      backgroundColor="#F8F9FC"
    >
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
        <Collapse in={Boolean(inlineError)}>
          <Alert
            severity="error"
            onClose={() => setInlineError(null)}
            sx={{ mb: 2, borderRadius: '8px', fontSize: '13px' }}
          >
            {inlineError}
          </Alert>
        </Collapse>

        {isConnected ? (
          <>
            <Typography
              fontSize={14}
              fontWeight={400}
              lineHeight={1.5}
              letterSpacing={-0.14}
              color="text.secondary"
              mb={3}
            >
              This voice agent is currently linked to a WhatsApp agent. You can
              disconnect it below.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: '14px 16px',
                borderRadius: '8px',
                border: '1px solid #DCE0E7',
                backgroundColor: '#FFFFFF',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(37, 211, 102, 0.08)',
                  border: '1px solid rgba(37, 211, 102, 0.3)',
                  flexShrink: 0,
                }}
              >
                <WhatsappLogoIcon size={20} weight="fill" color="#25D366" />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: '#302B3F',
                    fontSize: '14px',
                    fontWeight: 600,
                    letterSpacing: '-0.14px',
                    mb: 0.25,
                  }}
                >
                  {connectedAgentName}
                </Typography>
                <Typography
                  sx={{
                    color: '#6B6F80',
                    fontSize: '12px',
                    fontWeight: 400,
                    letterSpacing: '-0.12px',
                  }}
                >
                  WhatsApp Agent
                </Typography>
              </Box>
              <Box
                sx={{
                  ml: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '20px',
                  backgroundColor: 'rgba(37, 211, 102, 0.1)',
                }}
              >
                <LinkIcon size={12} color="#25D366" />
                <Typography
                  sx={{ fontSize: '11px', fontWeight: 500, color: '#25D366' }}
                >
                  Connected
                </Typography>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Typography
              fontSize={14}
              fontWeight={400}
              lineHeight={1.5}
              letterSpacing={-0.14}
              color="text.secondary"
              mb={3}
            >
              Select a WhatsApp agent to link with this voice agent. They will
              share conversation context across channels.
            </Typography>

            {isLoading ? (
              <Skeleton
                variant="rectangular"
                height={44}
                sx={{ borderRadius: '8px', mb: 3 }}
              />
            ) : whatsappAgents.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  py: 3,
                  mb: 3,
                  borderRadius: '8px',
                  border: '1px dashed #DCE0E7',
                  backgroundColor: '#FFFFFF',
                }}
              >
                <WhatsappLogoIcon size={28} weight="fill" color="#B0B4C1" />
                <Typography
                  sx={{ fontSize: '13px', color: '#B0B4C1', fontWeight: 400 }}
                >
                  No WhatsApp agents found
                </Typography>
              </Box>
            ) : (
              <Select
                value={selectedAgentId}
                onChange={e => setSelectedAgentId(e.target.value)}
                displayEmpty
                fullWidth
                size="small"
                sx={{
                  mb: 3,
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                  fontSize: '14px',
                  color: selectedAgentId ? '#302B3F' : '#B0B4C1',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#DCE0E7',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#B0B4C1',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6E61F0',
                  },
                }}
              >
                <MenuItem
                  value=""
                  disabled
                  sx={{ fontSize: '14px', color: '#B0B4C1' }}
                >
                  Select a WhatsApp agent
                </MenuItem>
                {whatsappAgents.map((agent: any) => (
                  <MenuItem
                    key={agent._id}
                    value={agent._id}
                    sx={{ fontSize: '14px', color: '#302B3F' }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <WhatsappLogoIcon
                        size={16}
                        weight="fill"
                        color="#25D366"
                      />
                      {agent.agentName}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            )}
          </>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            pt: 2,
            borderTop: '1px solid #DCE0E7',
            mx: -2,
            px: 2,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              flex: 1,
              color: 'custom.textBlue',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            Cancel
          </Button>

          {isConnected ? (
            <Button
              variant="contained"
              onClick={handleDisconnect}
              disabled={isDisconnectPending}
              startIcon={<LinkBreakIcon size={16} />}
              sx={{
                textTransform: 'none',
                borderRadius: '6px',
                flex: 1,
                backgroundColor: '#D32F2F',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#B71C1C' },
                '&:disabled': { backgroundColor: '#B0B4C1' },
              }}
            >
              {isDisconnectPending ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleConnect}
              disabled={
                !selectedAgentId ||
                isConnectPending ||
                whatsappAgents.length === 0
              }
              startIcon={<LinkIcon size={16} />}
              sx={{
                textTransform: 'none',
                borderRadius: '6px',
                flex: 1,
                backgroundColor: '#6E61F0',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#5A4FD4' },
                '&:disabled': { backgroundColor: '#B0B4C1' },
              }}
            >
              {isConnectPending ? 'Connecting...' : 'Connect'}
            </Button>
          )}
        </Box>
      </Box>
    </ReusableModal>
  );
};

export default ConnectWhatsappAgentModal;
